import { QUALIFYING_STAGE_META, qualifyingStage } from '../../components/screenings/qualifyingAgentStatus';

/**
 * The candidates table's Status filter. One place decides which options it offers and which rows
 * match, so the filter, its live "N of M match" count and the active-filter badge cannot disagree.
 *
 * Two vocabularies, because the Status column has two:
 *  - Most screenings: the server's `c.status` (Completed · In Progress · Invited · Pending Documents).
 *  - A qualifying-only screening (`screeningMeta.qualifyingOnly`): the column shows the Qualifying
 *    Agent stage (`qualifyingStage`). Filtering there on `c.status` was wrong — the server marks
 *    every *terminal* session 'Completed', declined and abandoned included, so "Completed"
 *    matched Not interested and No response rows too (26 Oct: 158 of 158 matched).
 *
 * Qualifying values carry a `qualifying:` prefix so a value means the same thing on any screening
 * and can never be confused with the server's 'Completed'. The filter value is a string (one
 * generic status) or an array (several qualifying stages, matched with OR).
 */

export const GENERIC_STATUS_OPTIONS = ['Completed', 'In Progress', 'Invited', 'Pending Documents'];

const PENDING_DOCUMENTS = 'Pending Documents';
const QUALIFYING_PREFIX = 'qualifying:';

export const qualifyingStatusValue = (stageKey) => `${QUALIFYING_PREFIX}${stageKey}`;

/** The selected values as an array, whatever shape the filter state holds. */
export function statusFilterValues(status) {
  if (Array.isArray(status)) return status.filter((v) => typeof v === 'string' && v);
  return typeof status === 'string' && status ? [status] : [];
}

export function isStatusFilterActive(status) {
  return statusFilterValues(status).length > 0;
}

/** Whether the Status filter uses the Qualifying Agent stages on this screening. */
export function usesQualifyingStatusFilter(screeningMeta) {
  return screeningMeta?.qualifyingOnly === true;
}

function rowMatchesValue(c, value) {
  if (value.startsWith(QUALIFYING_PREFIX)) {
    // Read the stage itself, not the cell: the Selected and Rejected tabs show "Selected" /
    // "Rejected" in that column, and a recruiter filtering there still means the call's outcome.
    return qualifyingStage(c?.qualifying) === value.slice(QUALIFYING_PREFIX.length);
  }
  if (value === PENDING_DOCUMENTS) {
    return (c?.pendingReuploadDocuments?.length || 0) > 0 || c?.status === PENDING_DOCUMENTS;
  }
  return (c?.status || 'Invited') === value;
}

/** True when the row matches any selected status (or no status is selected). */
export function candidateMatchesStatusFilter(c, status) {
  const values = statusFilterValues(status);
  if (values.length === 0) return true;
  return values.some((v) => rowMatchesValue(c, v));
}

/**
 * The qualifying-only options, in funnel order, each with how many of `rows` it matches.
 * Every stage is listed, a zero count included, so the list does not reshuffle between visits.
 */
export function buildQualifyingStatusOptions(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const options = Object.entries(QUALIFYING_STAGE_META).map(([key, meta]) => {
    const value = qualifyingStatusValue(key);
    return {
      value,
      label: meta.label,
      count: list.filter((c) => rowMatchesValue(c, value)).length,
    };
  });
  const pendingDocs = list.filter((c) => rowMatchesValue(c, PENDING_DOCUMENTS)).length;
  if (pendingDocs > 0) options.push({ value: PENDING_DOCUMENTS, label: PENDING_DOCUMENTS, count: pendingDocs });
  return options;
}
